import { inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, filter, map, noop, Observable } from 'rxjs';
import { DialogDefaultData } from 'src/app/interfaces';
import { DialogConfirmComponent } from 'src/app/notifications/dialogs/dialog-confirm/dialog-confirm.component';
import { DIALOG_DEFAULT_DATA } from 'src/app/notifications/dialogs/dialog-default/dialog-default.component';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { createProvider, MaybeNull } from 'src/app/utils';
import { completeSubject } from '../app.helpers';
import { CurrentOperation, OperationInfo } from './long-operations.interface';

@Injectable()
export class OperationInfoService implements OnDestroy {
  private operationTrigger$: BehaviorSubject<MaybeNull<OperationInfo>> = new BehaviorSubject<MaybeNull<OperationInfo>>(
    null,
  );
  private inProgressTrigger$: BehaviorSubject<CurrentOperation> = new BehaviorSubject<CurrentOperation>({
    operation: {},
    status: false,
  });

  private openedDialog: MaybeNull<DialogConfirmComponent> = null;

  private modalService = inject(NgbModal);
  private informService = inject(NotificationService);

  public ngOnDestroy(): void {
    completeSubject(this.operationTrigger$);
    completeSubject(this.inProgressTrigger$);
  }

  public get isLoadingGlobalSpinner$(): Observable<boolean> {
    return this.inProgressTrigger$.pipe(
      filter(({ operation }: CurrentOperation) => operation.spinner === 'global'),
      map(({ status }: CurrentOperation) => status),
    );
  }

  public get isLoadingLocallSpinner$(): Observable<boolean> {
    return this.inProgressTrigger$.pipe(
      filter(({ operation }: CurrentOperation) => operation.spinner === 'local'),
      map(({ status }: CurrentOperation) => status),
    );
  }

  public get isLoadingButtonSpinner$(): Observable<CurrentOperation> {
    return this.inProgressTrigger$.pipe(filter(({ operation }: CurrentOperation) => operation.spinner === 'button'));
  }

  public get operationInProgress$(): Observable<CurrentOperation> {
    return this.inProgressTrigger$.asObservable();
  }

  public get operationScheduled$(): Observable<MaybeNull<OperationInfo>> {
    return this.operationTrigger$.asObservable();
  }

  public startLoading(operation: OperationInfo): void {
    this.inProgressTrigger$.next({ operation, status: true });

    if (this.openedDialog && operation.spinner === 'global') {
      this.openedDialog.closeDialog();
    }
  }

  public stopLoading(operation: OperationInfo): void {
    this.inProgressTrigger$.next({ operation, status: false });
  }

  public setupOperation(operation: OperationInfo, action: () => void = noop): void {
    if (!operation.confirmationDialog) {
      this.startLoading(operation);
      this.operationTrigger$.next(operation);
      action();

      return;
    }

    this.openDialog(operation.confirmationDialog)
      .then(() => {
        this.startLoading(operation);
        this.operationTrigger$.next(operation);

        action();
      })
      .catch(() => {
        // the operation hasn't been confirmed
      });
  }

  public nullifyOperation(operation: OperationInfo): void {
    this.stopLoading(operation);
    this.operationTrigger$.next(null);

    if (this.openedDialog) {
      this.openedDialog.closeDialog();
    }
  }

  public showSuccessfulMessage(operation: OperationInfo): void {
    if (operation.successfulMessage) {
      this.informService.showSuccessfulMessage(operation.successfulMessage);
    }
  }

  private async openDialog(data: DialogDefaultData): Promise<void> {
    const dialog: DialogConfirmComponent = this.modalService.open(DialogConfirmComponent, {
      centered: true,
      injector: Injector.create({
        providers: [createProvider(DIALOG_DEFAULT_DATA, { useValue: data })],
      }),
    }).componentInstance as DialogConfirmComponent;

    this.openedDialog = dialog;

    return new Promise<void>((resolve: () => void, reject: () => void) => {
      dialog.onConfirm.subscribe(() => {
        resolve();
      });

      dialog.onCancel.subscribe(() => {
        reject();
      });
    });
  }
}
